import { UrlMappingMatchType } from "~/src/__generated__/schema";

export type ConditionRowKind =
  | "query_param"
  | "path"
  | "query_substring"
  | "referrer"
  | "title"
  | "environment";

export type ConditionRow = {
  kind: ConditionRowKind;
  key: string;
  value: string;
  matchMode: string;
};

export type UrlMappingConditionsFormValues = {
  conditionRows: ConditionRow[];
};

export const parseSourceUrls = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

export const sourceUrlsToText = (
  sourceUrl: string | null | undefined,
  conditions: Record<string, unknown> | null | undefined
): string => {
  const sourceUrls = conditions?.source_urls;
  const fromConditions = Array.isArray(sourceUrls)
    ? (sourceUrls as string[])
    : [];
  if (fromConditions.length > 0) {
    return fromConditions.join("\n");
  }
  return sourceUrl || "";
};

export type UrlMappingMutationInputValues = {
  name: string;
  active: boolean;
  matchType: UrlMappingMatchType;
  priority: number;
  sourceUrl: string;
  sourceUrls: string;
  sourceHost: string;
  sourcePath: string;
  regexPattern: string;
  destinationUrl: string;
  destinationMode: "STATIC" | "TEMPLATE";
  conditionRows: ConditionRow[];
  startsAt: Date | null;
  endsAt: Date | null;
};

export const buildUrlMappingMutationInput = (
  values: UrlMappingMutationInputValues
) => {
  const destinationMode =
    values.destinationMode || inferDestinationMode(values.destinationUrl);
  const conditions = conditionRowsToJson(values.conditionRows) as Record<
    string,
    unknown
  >;

  let sourceUrl: string | null = values.sourceUrl || null;
  const sourceHost: string | null = values.sourceHost || null;
  const sourcePath: string | null = values.sourcePath || null;
  const regexPattern: string | null = values.regexPattern || null;

  if (values.matchType === "EXACT") {
    const urls = parseSourceUrls(values.sourceUrls);
    if (urls.length === 0) {
      sourceUrl = null;
    } else if (urls.length === 1) {
      sourceUrl = urls[0];
      delete conditions.source_urls;
    } else {
      sourceUrl = urls[0];
      conditions.source_urls = urls;
    }
  } else {
    delete conditions.source_urls;
  }

  return {
    name: values.name || null,
    matchType: values.matchType,
    priority: Number(values.priority),
    sourceUrl,
    sourceHost,
    sourcePath,
    regexPattern,
    destinationUrl: values.destinationUrl,
    destinationMode,
    conditions,
    active: values.active,
    startsAt: values.startsAt ? values.startsAt.toISOString() : null,
    endsAt: values.endsAt ? values.endsAt.toISOString() : null,
  };
};

export const emptyConditionRow = (): ConditionRow => ({
  kind: "query_param",
  key: "",
  value: "",
  matchMode: "exact",
});

export const emptyConditionsFormValues =
  (): UrlMappingConditionsFormValues => ({
    conditionRows: [],
  });

export const conditionRowKindLabels: Record<ConditionRowKind, string> = {
  query_param: "クエリパラメータ",
  path: "パス",
  query_substring: "クエリ（部分一致）",
  referrer: "Referrer",
  title: "タイトル",
  environment: "環境",
};

export const conditionRowKindOptions = Object.entries(
  conditionRowKindLabels
).map(([value, label]) => ({ value, label }));

export const defaultMatchModeForKind = (kind: ConditionRowKind): string => {
  switch (kind) {
    case "query_param":
      return "exact";
    case "path":
    case "query_substring":
    case "referrer":
      return "contains";
    case "title":
      return "equals";
    case "environment":
      return "equals";
    default:
      return "exact";
  }
};

export const matchModeOptionsForKind = (
  kind: ConditionRowKind
): { value: string; label: string }[] => {
  switch (kind) {
    case "query_param":
      return [
        { value: "exact", label: "完全一致" },
        { value: "regex", label: "正規表現" },
      ];
    case "path":
      return [
        { value: "prefix", label: "前方一致" },
        { value: "contains", label: "含む" },
        { value: "excludes", label: "除外" },
      ];
    case "query_substring":
      return [
        { value: "contains", label: "含む" },
        { value: "excludes", label: "除外" },
      ];
    case "referrer":
      return [{ value: "contains", label: "含む" }];
    case "title":
      return [{ value: "equals", label: "完全一致" }];
    case "environment":
      return [{ value: "equals", label: "完全一致" }];
    default:
      return [{ value: "exact", label: "完全一致" }];
  }
};

export const conditionRowsToJson = (
  rows: ConditionRow[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  const pathContains: string[] = [];
  const pathExcludes: string[] = [];
  const queryContains: string[] = [];
  const queryExcludes: string[] = [];
  const referrerContains: string[] = [];
  const titleEquals: string[] = [];
  const environments: string[] = [];
  const queryParams: Record<string, string> = {};
  const queryParamsRegex: Record<string, string | string[]> = {};
  let pathPrefix: string | undefined;

  const appendRegexPatterns = (key: string, patterns: string[]) => {
    if (patterns.length === 0) return;

    const existing = queryParamsRegex[key];
    if (!existing) {
      queryParamsRegex[key] = patterns.length === 1 ? patterns[0] : patterns;
      return;
    }

    const merged = [
      ...(Array.isArray(existing) ? existing : [existing]),
      ...patterns,
    ];
    queryParamsRegex[key] = merged.length === 1 ? merged[0] : merged;
  };

  rows.forEach((row) => {
    const value = row.value.trim();
    const key = row.key.trim();

    switch (row.kind) {
      case "query_param":
        if (!key) return;
        if (row.matchMode === "regex") {
          if (value) appendRegexPatterns(key, [value]);
        } else if (value) {
          queryParams[key] = value;
        }
        break;
      case "path":
        if (!value) return;
        if (row.matchMode === "prefix") pathPrefix = value;
        else if (row.matchMode === "excludes") pathExcludes.push(value);
        else pathContains.push(value);
        break;
      case "query_substring":
        if (!value) return;
        if (row.matchMode === "excludes") queryExcludes.push(value);
        else queryContains.push(value);
        break;
      case "referrer":
        if (value) referrerContains.push(value);
        break;
      case "title":
        if (value) titleEquals.push(value);
        break;
      case "environment":
        if (value) {
          value
            .split("|")
            .map((part) => part.trim())
            .filter(Boolean)
            .forEach((part) => environments.push(part));
        }
        break;
      default:
        break;
    }
  });

  if (pathContains.length > 0) result.path_contains = pathContains;
  if (pathPrefix) result.path_prefix = pathPrefix;
  if (pathExcludes.length > 0) result.path_excludes = pathExcludes;
  if (queryContains.length > 0) result.query_contains = queryContains;
  if (queryExcludes.length > 0) result.query_excludes = queryExcludes;
  if (referrerContains.length > 0) {
    result.referrer_contains = referrerContains;
  }
  if (titleEquals.length > 0) result.title_equals = titleEquals;
  if (environments.length > 0) {
    result.environments = Array.from(new Set(environments));
  }
  if (Object.keys(queryParams).length > 0) result.query_params = queryParams;
  if (Object.keys(queryParamsRegex).length > 0) {
    result.query_params_regex = queryParamsRegex;
  }

  return result;
};

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const exactValuesToRegexPattern = (values: string[]): string => {
  const patterns = values.map((value) => `^${escapeRegex(value)}$`);
  return patterns.join("|");
};

export const conditionRowsFromJson = (
  conditions: Record<string, unknown> | null | undefined
): ConditionRow[] => {
  if (!conditions || typeof conditions !== "object") return [];

  const rows: ConditionRow[] = [];

  if (typeof conditions.path_prefix === "string" && conditions.path_prefix) {
    rows.push({
      kind: "path",
      key: "",
      value: conditions.path_prefix,
      matchMode: "prefix",
    });
  }

  const pushArrayRows = (
    values: unknown,
    kind: ConditionRowKind,
    matchMode: string
  ) => {
    if (!Array.isArray(values)) return;
    values.forEach((value) => {
      if (typeof value === "string" && value.trim()) {
        rows.push({
          kind,
          key: "",
          value,
          matchMode,
        });
      }
    });
  };

  pushArrayRows(conditions.path_contains, "path", "contains");
  pushArrayRows(conditions.path_excludes, "path", "excludes");
  pushArrayRows(conditions.query_contains, "query_substring", "contains");
  pushArrayRows(conditions.query_excludes, "query_substring", "excludes");
  pushArrayRows(conditions.referrer_contains, "referrer", "contains");
  pushArrayRows(conditions.title_equals, "title", "equals");
  pushArrayRows(conditions.environments, "environment", "equals");

  const queryParamsRaw = conditions.query_params;
  if (queryParamsRaw && typeof queryParamsRaw === "object") {
    Object.entries(queryParamsRaw as Record<string, string>).forEach(
      ([key, value]) => {
        rows.push({
          kind: "query_param",
          key,
          value: String(value),
          matchMode: "exact",
        });
      }
    );
  }

  const queryParamsAnyRaw = conditions.query_params_any;
  if (queryParamsAnyRaw && typeof queryParamsAnyRaw === "object") {
    Object.entries(queryParamsAnyRaw as Record<string, string[]>).forEach(
      ([key, values]) => {
        rows.push({
          kind: "query_param",
          key,
          value: exactValuesToRegexPattern(values),
          matchMode: "regex",
        });
      }
    );
  }

  const queryParamsRegexRaw = conditions.query_params_regex;
  if (queryParamsRegexRaw && typeof queryParamsRegexRaw === "object") {
    Object.entries(
      queryParamsRegexRaw as Record<string, string | string[]>
    ).forEach(([key, patterns]) => {
      const values = Array.isArray(patterns) ? patterns : [patterns];
      rows.push({
        kind: "query_param",
        key,
        value: values.join("|"),
        matchMode: "regex",
      });
    });
  }

  return rows;
};

export type KnownUrlMappingMatchType = Exclude<
  UrlMappingMatchType,
  "%future added value"
>;

export const matchTypeLabels: Record<KnownUrlMappingMatchType, string> = {
  EXACT: "完全一致",
  PREFIX: "前方一致",
  REGEX: "正規表現",
  HOST_PATH: "ホスト+パス",
  TRANSFORM: "Transform（条件のみ）",
};

export type MatchTypeHelp = {
  title: string;
  description: string;
  bullets: string[];
  example?: string;
};

export const matchTypeHelp: Record<KnownUrlMappingMatchType, MatchTypeHelp> = {
  EXACT: {
    title: "完全一致",
    description:
      "ソースURLと完全に一致するURLのみマッチします。複数URLは1行1件で入力できます。",
    bullets: [
      "1行1件 — 複数行で同じ宛先URLにまとめられます",
      "scheme + host + path + query を含むフルURL",
    ],
  },
  PREFIX: {
    title: "前方一致",
    description: "ソースURLで始まるURLにマッチします。",
    bullets: ["パス配下の複数URLを1ルールで扱う場合に使います"],
  },
  REGEX: {
    title: "正規表現",
    description: "正規表現パターンにマッチしたURLを置換します。",
    bullets: [
      "宛先URLで \\1, \\2 などキャプチャグループを参照できます",
      "追加条件と併用可能です",
    ],
  },
  HOST_PATH: {
    title: "ホスト+パス",
    description: "ホストとパスの組み合わせでマッチします。",
    bullets: [
      "ソースパス末尾に * を付けると前方一致（例: /shopping/lp/*）",
      "クエリパラメータはパスに含めません",
    ],
  },
  TRANSFORM: {
    title: "Transform（条件のみ）",
    description:
      "ソースURLは不要です。下の「追加条件」だけでマッチ対象を指定します。",
    bullets: [
      "条件を1つも設定しないとすべてのURLにマッチします（通常は1つ以上設定）",
      "宛先モードは「テンプレート」が一般的です",
      "テンプレート変数: {origin} {path} {query_string} {pub} {on}",
      "追加条件はすべて AND — 行を追加して組み合わせられます",
    ],
    example:
      "例: path_contains = example.com、query_params p = sample_product → 宛先 https://example.com/shopping/lp/mapped{query_string}",
  },
};

export const destinationModeHelp = {
  STATIC: "固定URL — マッチ時に宛先URLをそのまま返します",
  TEMPLATE:
    "テンプレート — {origin} {path} {query_string} などを展開します（Transform でよく使用）",
};

export const matchTypeOptions = Object.entries(matchTypeLabels).map(
  ([value, label]) => ({ value, label })
);

export const destinationModeOptions = [
  { label: "固定URL", value: "STATIC" },
  { label: "テンプレート", value: "TEMPLATE" },
];

export const inferDestinationMode = (
  destinationUrl: string
): "STATIC" | "TEMPLATE" =>
  destinationUrl.includes("{") ? "TEMPLATE" : "STATIC";

export const formatSourceSummary = (urlMapping: {
  matchType: UrlMappingMatchType;
  sourceUrl?: string | null;
  sourceHost?: string | null;
  sourcePath?: string | null;
  regexPattern?: string | null;
  conditions?: Record<string, unknown> | null;
}): string => {
  switch (urlMapping.matchType) {
    case "EXACT": {
      const urls = Array.isArray(urlMapping.conditions?.source_urls)
        ? (urlMapping.conditions?.source_urls as string[])
        : [];
      if (urls.length > 1) {
        return `${urls.length}件: ${urls[0]}`;
      }
      return urlMapping.sourceUrl || "-";
    }
    case "PREFIX":
      return urlMapping.sourceUrl || "-";
    case "REGEX":
    case "HOST_PATH":
      return (
        [urlMapping.sourceHost, urlMapping.sourcePath]
          .filter(Boolean)
          .join("") || "-"
      );
    case "TRANSFORM":
      return "(conditions)";
    default:
      return "-";
  }
};

export const truncateText = (text: string, maxLength = 60): string =>
  text.length <= maxLength ? text : `${text.slice(0, maxLength)}…`;
